import { sanitizeAttribution } from "./marketing";
import { PUBLIC_ROUTE_CONFIGS } from "./public-routes";

export type LeadKind = "contact" | "quote" | "erp" | "funnel";
export interface ReportLead {
  kind: LeadKind;
  id: string;
  createdAt: Date | null;
  status: string;
  attribution: unknown;
}

// A submitted, persisted CRM row is the only confirmed lead. Clicks, form
// starts, thank-you page views and failed requests never enter this dataset.
export const LEAD_DEFINITIONS = {
  confirmed: "Richiesta salvata nel CRM dopo un invio riuscito (una per tipo e ID).",
  qualified: "Funnel: qualificato/call prenotata/proposta/incarico; altri form: meeting/vinto.",
  opportunity: "Funnel: call prenotata/proposta/incarico; altri form: meeting/vinto.",
} as const;

export function classifyLead(lead: ReportLead) {
  const funnel = lead.kind === "funnel";
  return {
    qualified: funnel
      ? ["qualificato", "call_prenotata", "proposta_inviata", "incarico_firmato"].includes(lead.status)
      : ["meeting", "vinto"].includes(lead.status),
    opportunity: funnel
      ? ["call_prenotata", "proposta_inviata", "incarico_firmato"].includes(lead.status)
      : ["meeting", "vinto"].includes(lead.status),
  };
}

// Only public paths are report dimensions: query strings can contain email,
// click IDs, or other personal information.
export function reportLanding(raw: string | undefined): string {
  if (!raw?.startsWith("/") || raw.startsWith("//")) return "non_attribuita";
  const path = raw.split(/[?#]/, 1)[0];
  // A path can itself contain personal data. Only known public pages are
  // report dimensions; dynamic insight slugs are grouped without the slug.
  if (PUBLIC_ROUTE_CONFIGS.some((route) => route.path === path)) return path;
  if (/^\/insights\/[a-z0-9-]{1,80}$/.test(path)) return "/insights/*";
  return "non_attribuita";
}

export function reportSource(raw: unknown): string {
  const a = sanitizeAttribution(raw);
  const medium = a?.utmMedium?.toLowerCase();
  const source = a?.utmSource?.toLowerCase();
  if (a?.gclid || a?.gbraid || a?.wbraid || medium === "cpc" || medium === "paid") return "paid";
  if (medium === "organic" || (!medium && source && /^(google|bing|yahoo|duckduckgo)$/.test(source))) return "organic";
  if (source) return ["linkedin", "facebook", "instagram", "newsletter", "email"].includes(source)
    ? `utm:${source}` : "utm:altra";
  try {
    const host = new URL(a?.referrer ?? "").hostname.toLowerCase();
    if (/^(www\.)?(google\.[a-z.]+|bing\.com|search\.yahoo\.com|duckduckgo\.com)$/.test(host)) return "organic";
  } catch { /* no referrer */ }
  return "non_attribuita";
}

export interface ReportRow {
  landing: string;
  source: string;
  confirmed: number;
  qualified: number;
  opportunities: number;
  smallSample: boolean;
}

export function aggregateLeads(leads: ReportLead[], now: Date, days: 30 | 60 | 90): ReportRow[] {
  const from = new Date(now.getTime() - days * 86400000);
  const seen = new Set<string>();
  const rows = new Map<string, ReportRow>();
  for (const lead of leads) {
    if (!lead.createdAt || lead.createdAt < from || lead.createdAt > now) continue;
    const identity = `${lead.kind}:${lead.id}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    const a = sanitizeAttribution(lead.attribution);
    const landing = reportLanding(a?.landingPage);
    const source = reportSource(a);
    const key = JSON.stringify([landing, source]);
    const row = rows.get(key) ?? { landing, source, confirmed: 0, qualified: 0, opportunities: 0, smallSample: true };
    const state = classifyLead(lead);
    row.confirmed++;
    row.qualified += Number(state.qualified);
    row.opportunities += Number(state.opportunity);
    row.smallSample = row.confirmed < 10;
    rows.set(key, row);
  }
  return Array.from(rows.values()).sort((a, b) => b.confirmed - a.confirmed || a.landing.localeCompare(b.landing));
}